import { useFlag } from '../../services/launchDarkly';
import { useStores } from '../../stores/useStores';

import { pinnedFlags } from './newLanderPins';

export { resetPinnedNewLanderFlags } from './newLanderPins';

/**
 * True when the current direct_su / direct_em lander should use the redesigned sign up flow.
 * Each lander has its own flag so they can be rolled out independently.
 */
export function useNewLanderSignUp() {
  const { authStore } = useStores();
  const isDirectSuEnabled = useFlag('marketing_landers_new_signup_direct_su', false);
  const isDirectEmEnabled = useFlag('marketing_landers_new_signup_direct_em', false);

  const isDirectSuLanding = authStore.isDirectSignUpLanding();
  const isDirectEmLanding = authStore.isDirectEmailLanding();

  if (isDirectSuLanding && pinnedFlags.su === undefined) pinnedFlags.su = isDirectSuEnabled;
  if (isDirectEmLanding && pinnedFlags.em === undefined) pinnedFlags.em = isDirectEmEnabled;

  const isNewSuLander = (pinnedFlags.su ?? isDirectSuEnabled) && isDirectSuLanding;
  const isNewEmLander = (pinnedFlags.em ?? isDirectEmEnabled) && isDirectEmLanding;

  // Stays true after the sign up closes and ?landing= is cleared (the pins outlive the param), and across
  // remounts, so the default landing's auto-open effects don't reopen the sign up.
  const wasNewLanderSignUp = Boolean(pinnedFlags.su || pinnedFlags.em);

  return {
    isNewSuLander,
    isNewEmLander,
    isNewLanderSignUp: isNewSuLander || isNewEmLander,
    wasNewLanderSignUp,
  };
}

/**
 * True when the direct_reg lander should use the redesigned flow: the redesigned welcome bonus and country
 * select modals, and the single terms checkbox on the sign up form. Pinned like the lander flags above, and
 * kept after the ?landing= param is cleared, since the welcome bonus opens once it is gone.
 */
export function useNewDirectRegSignUp() {
  const { authStore } = useStores();
  const isDirectRegEnabled = useFlag('marketing_landers_new_signup_direct_reg', false);

  if (authStore.isDirectRegLanding() && pinnedFlags.reg === undefined) pinnedFlags.reg = isDirectRegEnabled;

  return pinnedFlags.reg ?? isDirectRegEnabled;
}

/** True once the direct_reg lander ran on the redesigned flow in this page session (outlives ?landing=). */
export function useWasNewDirectRegSignUp() {
  return pinnedFlags.reg === true;
}

/**
 * sign_up_flow_country_selection as seen on the direct_reg lander. It decides both whether the sign up form drops
 * the marketing consents and whether the country select (where they now live) opens after sign up, so it is pinned
 * with the other direct_reg flags: a flip after identify would leave the player never asked for consents.
 */
export function useDirectRegCountrySelection() {
  const { authStore } = useStores();
  const isCountrySelectionEnabled = useFlag<boolean>('sign_up_flow_country_selection', false);

  if (authStore.isDirectRegLanding() && pinnedFlags.regCountrySelection === undefined) {
    pinnedFlags.regCountrySelection = isCountrySelectionEnabled;
  }

  return pinnedFlags.regCountrySelection ?? isCountrySelectionEnabled;
}
